import { createContext, useContext, useState, type ReactNode } from 'react';

interface SendBlockModalContextValue {
  // Email verification modal
  emailVerificationOpen: boolean;
  emailForVerification: string | null;
  showEmailVerificationModal: (email: string) => void;
  closeEmailVerificationModal: () => void;
  onEmailVerificationSuccess: () => void;

  // View-only block modal
  viewOnlyBlockOpen: boolean;
  showViewOnlyBlockModal: () => void;
  closeViewOnlyBlockModal: () => void;
}

const SendBlockModalContext = createContext<SendBlockModalContextValue | undefined>(undefined);

export function SendBlockModalProvider({ children }: { children: ReactNode }) {
  // Email verification modal state
  const [emailVerificationOpen, setEmailVerificationOpen] = useState(false);
  const [emailForVerification, setEmailForVerification] = useState<string | null>(null);

  // View-only block modal state
  const [viewOnlyBlockOpen, setViewOnlyBlockOpen] = useState(false);

  const showEmailVerificationModal = (email: string) => {
    setEmailForVerification(email);
    setEmailVerificationOpen(true);
  };

  const closeEmailVerificationModal = () => {
    setEmailVerificationOpen(false);
    setEmailForVerification(null);
  };

  const onEmailVerificationSuccess = () => {
    // This can be extended to trigger refetch of user data if needed
    closeEmailVerificationModal();
  };

  const showViewOnlyBlockModal = () => {
    setViewOnlyBlockOpen(true);
  };

  const closeViewOnlyBlockModal = () => {
    setViewOnlyBlockOpen(false);
  };

  return (
    <SendBlockModalContext.Provider
      value={{
        emailVerificationOpen,
        emailForVerification,
        showEmailVerificationModal,
        closeEmailVerificationModal,
        onEmailVerificationSuccess,
        viewOnlyBlockOpen,
        showViewOnlyBlockModal,
        closeViewOnlyBlockModal,
      }}
    >
      {children}
    </SendBlockModalContext.Provider>
  );
}

export function useSendBlockModal() {
  const context = useContext(SendBlockModalContext);
  if (!context) {
    throw new Error('useSendBlockModal must be used within SendBlockModalProvider');
  }
  return context;
}
